import {defineGlobalStyles} from '@chakra-ui/react3'
import {PAGE_BG_VAR} from '../components/navigation/header/header-variant'

export const globalCss = defineGlobalStyles({
    // The header bar's own footprint, published on :root by whichever header
    // renders — the Xleap header or the checkout header, each with the value it
    // owns.
    '@property --header-bar-block-size': {
        syntax: '"<length>"',
        inherits: 'true',
        initialValue: '0px'
    },
    // The message bar's own height, published on :root by
    // app/leap/components/message-bar. Inherits, so a portalled subtree — the
    // navigation drawer — reads the same value the page does.
    '@property --header-message-bar-block-size': {
        syntax: '"<length>"',
        inherits: 'true',
        initialValue: '0px'
    },
    // Height of the header bar while it is visible, 0px while it is hidden.
    // Set by app/leap/components/navigation/header/index.jsx. Registered so
    // sticky content can animate its `top` together with the header.
    '@property --header-revealed-block-size': {
        syntax: '"<length>"',
        inherits: 'true',
        initialValue: '0px'
    },
    // The space the page has to keep free at the top of the viewport: the bar
    // and the message bar for the inline variant, the message bar alone for the
    // floating one, which overlays the content instead of clearing it.
    '@property --header-clearance-block-size': {
        syntax: '"<length>"',
        inherits: 'true',
        initialValue: '0px'
    },
    // header layout variant the page asks for: 0 (or absent) = inline
    // (reserves space), 1 = floating (reserves no space, content passes
    // behind). Owned by
    // app/leap/components/navigation/header/header-variant.js. Non-inheriting:
    // only the gate below reads it, on the same :root it is published on.
    '@property --header-floating': {
        syntax: '"<number>"',
        inherits: 'false',
        initialValue: '0'
    },
    // The variant the breakpoint grants, derived from --header-floating below.
    // Registered separately so a consumer reading it gets a number rather than
    // the responsive var() it is assigned from, and inheriting so the header
    // recipe reads it deep in the tree.
    '@property --header-floating-active': {
        syntax: '"<number>"',
        inherits: 'true',
        initialValue: '0'
    },
    // The page's own background (STOK-17418), published on `:root` by
    // `HeaderVariant` (app/leap/components/navigation/header/header-variant.js)
    // and painted by the `html` rule below.
    //
    // Untyped and without an `initial-value` on purpose: a typed syntax requires
    // one, and it could only be a hardcoded literal (no `var()`, no token) that a
    // registered property would then always carry -- beating the `html` rule's
    // `var(..., {colors.bg})` fallback. Initial-less, the property stays
    // guaranteed-invalid until a page sets it, so that fallback still resolves.
    '@property --page-bg': {
        syntax: '"*"',
        inherits: 'true',
        initialValue: '{colors.bg}'
    },
    // Registered so it computes to a length: an unregistered custom property
    // would hand the header back its raw calc() token stream, which is not a
    // number it can compare a scroll offset against.
    '@property --header-hide-threshold': {
        syntax: '"<length>"',
        inherits: 'true',
        initialValue: '0px'
    },
    '*': {
        fontFeatureSettings: '"cv11"',
        '--ring-inset': 'var(--chakra-empty,/*!*/ /*!*/)',
        '--ring-offset-width': '0px',
        '--ring-offset-color': '#fff',
        '--ring-color': 'rgba(66, 153, 225, 0.6)',
        '--ring-offset-shadow': '0 0 #0000',
        '--ring-shadow': '0 0 #0000',
        '--brightness': 'var(--chakra-empty,/*!*/ /*!*/)',
        '--contrast': 'var(--chakra-empty,/*!*/ /*!*/)',
        '--grayscale': 'var(--chakra-empty,/*!*/ /*!*/)',
        '--hue-rotate': 'var(--chakra-empty,/*!*/ /*!*/)',
        '--invert': 'var(--chakra-empty,/*!*/ /*!*/)',
        '--saturate': 'var(--chakra-empty,/*!*/ /*!*/)',
        '--sepia': 'var(--chakra-empty,/*!*/ /*!*/)',
        '--drop-shadow': 'var(--chakra-empty,/*!*/ /*!*/)',
        '--backdrop-blur': 'var(--chakra-empty,/*!*/ /*!*/)',
        '--backdrop-brightness': 'var(--chakra-empty,/*!*/ /*!*/)',
        '--backdrop-contrast': 'var(--chakra-empty,/*!*/ /*!*/)',
        '--backdrop-grayscale': 'var(--chakra-empty,/*!*/ /*!*/)',
        '--backdrop-hue-rotate': 'var(--chakra-empty,/*!*/ /*!*/)',
        '--backdrop-invert': 'var(--chakra-empty,/*!*/ /*!*/)',
        '--backdrop-opacity': 'var(--chakra-empty,/*!*/ /*!*/)',
        '--backdrop-saturate': 'var(--chakra-empty,/*!*/ /*!*/)',
        '--backdrop-sepia': 'var(--chakra-empty,/*!*/ /*!*/)',
        '--global-font-mono': 'fonts.mono',
        '--global-font-body': 'fonts.body',
        '--global-color-border': 'colors.border'
    },
    html: {
        color: 'fg',
        // The page's own background (STOK-17418). `--page-bg` is published on
        // `:root` by `HeaderVariant`
        // (app/leap/components/navigation/header/header-variant.js) — for Xleap
        // template pages the color of the first module, so the page reads as one
        // surface from the top of the viewport down, behind the header and the
        // breadcrumb alike and through the whole scroll rather than only behind
        // the closed header.
        //
        // Declared here on `html` rather than on any component: it is the
        // canvas the browser paints the whole viewport with, including the
        // overscroll area, and it is the one element `HeaderVariant` already
        // publishes onto. The fallback resolves the theme-aware `bg` token, so
        // `data-leap-theme=dark` still darkens a page that declares no color of
        // its own — see the `@property --page-bg` block above for why that
        // registration carries no `initial-value` to keep this reachable.
        bg: `var(${PAGE_BG_VAR})`,
        lineHeight: '1.5',
        colorPalette: 'gray'
    },
    // Chakra v2's global styles (app/theme/foundations/styles.js) paint `body`
    // an opaque `stokkeCore.white` over a `min-height: 100vh` box, which would
    // cover the canvas `html` paints above. Neutralized here rather than there
    // because that sheet also dresses the non-Leap pages.
    //
    // `inherit`, not `transparent`: every background declaration Chakra v3
    // emits also publishes `--bg-currentcolor`, the variable the breadcrumb's
    // overflow fade reads (app/leap/components/breadcrumb/index.recipe.js) to
    // fade into whatever is behind it. `inherit` carries `html`'s resolved
    // color down that chain; `transparent` would paint the same but hand the
    // fade a transparent gradient stop.
    //
    // `html body`, not `body`, so it outweighs the v2 rule on specificity
    // rather than on which of the two Chakra versions happens to inject its
    // global styles last.
    'html body': {
        backgroundColor: 'inherit'
    },
    ':root': {
        // The height the design gives the Xleap header bar
        // (app/leap/components/navigation), at every breakpoint. `barInner` in
        // the header recipe sizes the rendered bar from it, so the bar the page
        // reserves and the bar it paints are one box: the flyout hangs below the
        // paint but is sized from the reservation, and any difference between
        // them is a strip of backdrop at the foot of the panel (STOK-17509).
        '--header-bar-content-block-size': '4rem', // 64px
        // Card chrome the header adds around that bar from the Leap `s`
        // breakpoint up, where it renders as an inset card: a block inset above
        // and below it plus the card's own borders. Both are 0 below `s`, where
        // the bar is full-bleed. They live here, next to the bar height, because
        // the Xleap footprint below has to carry them; the header recipe
        // (app/leap/components/navigation/header/index.recipe.js) sizes the
        // rendered card from the same vars, so the reservation and the render
        // cannot drift apart.
        '--header-card-border-width': {base: '0px', s: '1px'},
        // Literal, like the rest of this chain: `spacing-20`, the token the card
        // is inset by in app/leap/components/navigation/header/index.recipe.js.
        '--header-card-offset-block': {base: '0px', s: '1.25rem'}, // 20px
        '--header-card-chrome-block-size':
            'calc(2 * var(--header-card-border-width) + 2 * var(--header-card-offset-block))',
        // The Xleap header's footprint (app/leap/components/navigation/header):
        // the bar and the card chrome around it. Published into
        // --header-bar-block-size while it renders.
        '--xleap-header-bar-block-size':
            'calc(var(--header-bar-content-block-size) + var(--header-card-chrome-block-size))',
        // The checkout header's footprint
        // (app/leap/components/navigation/checkout-header), published into
        // --header-bar-block-size while it renders. Carried over unchanged as
        // one flat literal, and so 46px over the card it actually paints at
        // `base` and 4px over it from `s` — that card is built from the same
        // bar and chrome as the Xleap one, and STOK-17581 aligns the two.
        '--checkout-header-bar-block-size': '6.875rem', // 110px
        // The legacy CMS message bar's footprint (app/leap/components/message-bar),
        // published into --header-message-bar-block-size while it renders.
        // Mirrors the CMS template's literal 64px/44px — px, since that bar does
        // not scale with the root font size. The whole chain lives on the Leap
        // breakpoint scale for `s`, which the message bar varies on and which
        // only that scale carries.
        '--legacy-message-bar-block-size': {base: '64px', s: '44px'},
        // The Leap message bar module's footprint (app/leap/modules/message-bar),
        // published instead while that module renders. Same tokens as its recipe:
        // `spacing-44`, and `spacing-64` below `s` when a slide carries a CTA,
        // which sits below the copy there.
        '--leap-message-bar-block-size': '{spacing.spacing-44}',
        '--leap-message-bar-cta-block-size': {
            base: '{spacing.spacing-64}',
            s: '{spacing.spacing-44}'
        },
        // Floating is granted only from `s` up, where the bar renders as an
        // inset card. Below `s` it is a full-bleed band the page has to clear:
        // overlaying there crops the top of the first module (STOK-17610).
        '--header-floating-active': {base: '0', s: 'var(--header-floating)'},
        // The bar term drops out for the floating variant, which overlays the
        // page content rather than clearing room for it. The message bar sits
        // above the bar in flow either way, so it always counts.
        '--header-clearance-block-size':
            'calc(var(--header-message-bar-block-size) + var(--header-bar-block-size) * (1 - var(--header-floating-active)))',
        // Scroll offset at which the header's placement area — the slot it
        // occupies in flow, message bar included — has left the viewport, and so
        // the point from which the header starts auto-hiding on scroll (read by
        // app/leap/components/navigation/header/index.jsx). That is the
        // clearance, so the floating variant waits on the message bar alone and
        // a page without one falls back to the floor.
        // The 8px floor absorbs the pixel or two the page shifts by when the
        // header opens, which would otherwise read as a scroll down.
        '--header-hide-threshold': 'max(8px, var(--header-clearance-block-size))'
    },
    '*::placeholder, *[data-placeholder]': {
        color: 'fg.muted/80'
    },
    // text selection has no custom background - colorPalette.* cannot resolve at the html
    // scope it is declared on, so the browser default applies
    // workaround for mobile chrome bug where the scrollbar of non-root element hides only after first scroll event
    '@media (pointer: coarse)': {
        '[data-hide-scrollbar]': {
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
                display: 'none'
            }
        }
    },
    global: {
        // support for content-sized fluid designs - currently only text styles use it
        '@property --fluid-unit': {
            syntax: '"<length>"',
            inherits: 'true',
            initialValue: '1vw'
        }
    }
})
